import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Warning, CaretDown, CaretRight, ShieldWarning } from "@phosphor-icons/react";

/**
 * Fetches NHTSA open recalls for the given year/make/model and shows a compact
 * heads-up card with expandable details. Renders nothing if no recalls.
 */
export default function RecallsPanel({ year, make, model }) {
  const [recalls, setRecalls] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!year || !make || !model) { setRecalls(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/recalls`, { params: { year, make, model } });
        if (!cancelled) setRecalls(data);
      } catch {
        if (!cancelled) setRecalls({ count: 0, recalls: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, make, model]);

  if (loading) {
    return (
      <div className="mt-4 border border-border rounded-md p-4 bg-card scan-line" data-testid="recalls-loading">
        <div className="font-mono-tech text-xs text-muted-foreground uppercase tracking-widest">// checking NHTSA open recalls…</div>
      </div>
    );
  }
  if (!recalls) return null;
  if (recalls.count === 0) {
    return (
      <div className="mt-4 border border-border rounded-md p-4 bg-background flex items-center gap-3" data-testid="recalls-empty">
        <div className="w-9 h-9 rounded-md bg-green-500/10 border border-green-500/40 flex items-center justify-center text-green-400">
          <ShieldWarning weight="bold" className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg leading-tight">No open recalls</div>
          <div className="text-xs text-muted-foreground font-mono-tech">Cleared against NHTSA for {year} {make} {model}</div>
        </div>
      </div>
    );
  }

  const list = showAll ? recalls.recalls : recalls.recalls.slice(0, 3);

  return (
    <div className="mt-4 border border-yellow-500/40 bg-yellow-500/5 rounded-md p-4" data-testid="recalls-panel">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-md bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center text-yellow-400">
          <Warning weight="fill" className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-lg leading-tight text-yellow-100">
            {recalls.count} open NHTSA recall{recalls.count === 1 ? "" : "s"} for this vehicle
          </div>
          <div className="text-[11px] font-mono-tech text-yellow-200/60 uppercase tracking-widest">
            Heads-up before you start diagnostics
          </div>
        </div>
      </div>

      <ul className="mt-3 space-y-2" data-testid="recalls-list">
        {list.map((r, i) => {
          const open = expandedIdx === i;
          return (
            <li key={r.campaign + i} className="border border-yellow-500/25 rounded-md bg-background overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedIdx(open ? null : i)}
                className="w-full text-left px-3 py-2 flex items-center justify-between gap-3 hover:bg-yellow-500/5 transition-colors"
                data-testid={`recall-toggle-${i}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono-tech text-yellow-300">{r.campaign}</div>
                  <div className="text-sm text-white/90 truncate">{r.component}</div>
                </div>
                {open ? <CaretDown weight="bold" className="w-4 h-4 text-muted-foreground" /> : <CaretRight weight="bold" className="w-4 h-4 text-muted-foreground" />}
              </button>
              {open && (
                <div className="px-3 pb-3 pt-1 text-xs text-white/85 space-y-2 border-t border-yellow-500/20">
                  <Field label="Summary">{r.summary}</Field>
                  {r.consequence && <Field label="Consequence">{r.consequence}</Field>}
                  {r.remedy && <Field label="Remedy">{r.remedy}</Field>}
                  <div className="text-[10px] font-mono-tech text-muted-foreground pt-1">
                    {r.manufacturer} · Reported {r.report_received_date?.slice(0, 10) || "—"}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {recalls.count > 3 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 text-xs text-yellow-300 hover:underline font-mono-tech"
          data-testid="recalls-toggle-all"
        >
          {showAll ? `Show first 3 only` : `Show all ${recalls.count} recalls →`}
        </button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono-tech text-muted-foreground uppercase tracking-widest">{label}</div>
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}
